// Admin FX — the USD→HTG rate the platform books at. Setting a rate writes a
// 'manual' row that's current immediately; new quotes snapshot it, so existing
// requests are never re-priced. Used by quotes, receipts (≈ USD), and payouts.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader } from '../../components/shared/primitives';
import { DataTable, type Column } from '../../components/shared/DataTable';
import { Button } from '../../components/stripe-ui/Button';
import { ADMIN_NAV } from '../_navs';
import { admin, type FxRateRow } from '../../lib/api';

export function AdminFxPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'fx'], queryFn: admin.fxRates });
  const [rate, setRate] = useState('');

  const setMut = useMutation({
    mutationFn: () => admin.setFxRate(Number(rate)),
    onSuccess: () => { setRate(''); qc.invalidateQueries({ queryKey: ['admin', 'fx'] }); toast.success('Rate updated'); },
    onError: (e: Error) => toast.error(e.message || 'Failed'),
  });

  const current = q.data?.current;
  const history = q.data?.history ?? [];

  // The design also specifies "Quotes locked" and "Set by" columns. Neither
  // has a backing field — FxRateRow carries no quote count and no actor — so
  // they are omitted rather than filled with a plausible number. The audit
  // log already answers "who set this".
  const columns: Column<FxRateRow>[] = [
    {
      key: 'effective', header: 'Effective', type: 'muted', width: '180px',
      value: (r) => new Date(r.effective_at).toLocaleString(),
    },
    {
      key: 'rate', header: 'Rate', type: 'mono', width: '140px',
      value: (r) => (
        <span>
          {Number(r.rate).toFixed(2)}
          {r.id === history[0]?.id && (
            <span className="ml-2 font-sans text-[10px] font-semibold text-[var(--color-success-fg)]">CURRENT</span>
          )}
        </span>
      ),
    },
    {
      key: 'pair', header: 'Pair', type: 'text', width: '120px',
      value: (r) => `${r.base} → ${r.quote}`,
    },
    {
      key: 'source', header: 'Source', type: 'text', width: 'minmax(0,1fr)',
      value: (r) => r.source,
    },
  ];

  return (
    <PortalShell nav={ADMIN_NAV} activeId="fx" title="FX rates" subtitle="The rate every quote locks to. Changes are audited.">
      <div className="space-y-5 max-w-3xl">
        {/* Current rate */}
        <Card className="bg-gradient-to-br from-brand-50 to-[var(--color-warning-bg)]/30 border-brand-100">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-700">Current rate</p>
          {q.isLoading ? (
            <p className="mt-2 text-[var(--color-muted)] text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</p>
          ) : (
            <>
              <p className="mt-1 text-[30px] font-bold tracking-tight text-[var(--color-ink)]">1 USD = <span className="text-brand-700">{current?.toFixed(2)}</span> HTG</p>
              <p className="text-[12px] text-[var(--color-secondary-2)]">
                Source: <span className="font-semibold">{q.data?.source}</span>
                {q.data?.effective_at ? ` · since ${new Date(q.data.effective_at).toLocaleString()}` : ' (no manual rate set — using fallback)'}
              </p>
            </>
          )}
        </Card>

        {/* Set rate */}
        <Card className="space-y-3">
          <SectionHeader title="Set a new rate" subtitle="Takes effect immediately for new quotes. Existing requests keep their snapshotted rate." />
          <div className="flex items-end gap-3 flex-wrap">
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">1 USD = ? HTG</span>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="number" step="0.01" value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  placeholder={current?.toFixed(2)}
                  className="w-40 px-3 py-2 text-[15px] font-mono border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
                <span className="text-[13px] text-[var(--color-secondary-2)]">HTG</span>
              </div>
            </label>
            <Button onClick={() => setMut.mutate()} loading={setMut.isPending} disabled={!rate || Number(rate) <= 0} leadingIcon={<Check className="w-3.5 h-3.5" />}>Set rate</Button>
          </div>
          <p className="text-[11px] text-[var(--color-muted)]">A daily market-rate feed can be wired later; until then this manual rate is authoritative.</p>
        </Card>

        <DataTable
          title="Rate history"
          subtitle="Every rate the platform has booked at. Changes are audited."
          summary={current != null ? [
            { label: 'Current rate', value: current.toFixed(2), hint: 'HTG per USD' },
            { label: 'Source', value: q.data?.source ?? '—', hint: q.data?.effective_at ? `Since ${new Date(q.data.effective_at).toLocaleDateString()}` : 'Fallback — no manual rate set' },
            { label: 'Changes logged', value: String(history.length), hint: 'Append-only' },
          ] : undefined}
          columns={columns}
          rows={history}
          rowKey={(r) => r.id}
          loading={q.isLoading}
          empty="No rate history. Set a rate above to start the log."
        />
      </div>
    </PortalShell>
  );
}
